<template>
  <section class="hg-listing-details">
    <ListingAccessories
      class="listing-accessories"
      :listingDefaultLocale="listingDefaultLocale"
      @virtualTourClick="emit('virtualTourClick')"
      @videoClick="emit('videoClick')"
      @documentClick="emit('documentClick')"
    >
      <template v-if="$slots.accessories" #default>
        <slot name="accessories"></slot>
      </template>
    </ListingAccessories>
    <template v-if="newConstructionProject">
      <NewBuildingProjectCardSticky
        :isHidden="isNewBuildingProjectCardInViewport"
        :class="$style.newBuildingProjectCardSticky"
        class="hide-on-print"
        @click="scrollTo('newBuildingProjectCard')"
      />
      <div id="newBuildingProjectCard" />
      <div ref="newBuildingProjectCardContainer">
        <NewBuildingProjectCard
          :availableDate="availableDate"
          :newProjectInfo="newConstructionProject"
          @scrollToOtherListings="emit('scrollToOtherListings')"
        />
      </div>
      <div id="newBuildingStickyBar" ref="newBuildingStickyBar" />
      <div :class="$style.newProjectCardContainer">
        <NewBuildingStickyBar
          :newProjectInfo="newConstructionProject"
          :isHidden="!isNewBuildingProjectCardOnTop"
        />
      </div>
    </template>
    <div :class="$style.spotlightContainer">
      <div class="spotlight-components">
        <component
          :is="spotlightComponent"
          v-for="(spotlightComponent, index) in spotlightComponents"
          :key="`spotlight-component-${index}`"
          :listing="listing"
          :listerBranding="listerBranding"
          @savePriceDropAlert="emit('savePriceDropAlert', $event)"
        />
      </div>
      <TenantPlusBadge v-if="isTenantPlusListing" :class="$style.tenantPlusBadge" />
    </div>
    <slot name="spotlight"></slot>
    <div>
      <CTANoAddress :address="listing.address" @onRequestAddress="emit('onRequestAddress')" />
      <AddressDetails v-if="listing.address" :address="listing.address" />
      <TravelTimeCustomPOI v-if="showTravelTimeCustomPOI" :listing="listing" />
      <TravelTime :listing="listing" />
      <slot name="address"></slot>
    </div>
    <div>
      <Costs
        :listing="listing"
        :listerBranding="listerBranding"
        @savePriceDropAlert="emit('savePriceDropAlert', $event)"
      />
      <slot name="financing"></slot>
    </div>
    <CoreAttributes :listing="listing" />
    <FeaturesFurnishings :listing="listing" />
    <ListingDocuments :listingDefaultLocale="listingDefaultLocale" />
    <slot name="features"></slot>
    <ListingStats
      v-if="showListingStats"
      :isTenantPlusSubscriber="isTenantPlusSubscriber"
      :listing="listing"
    />
    <NeighbourhoodSection
      v-if="
        enableNeighbourhood &&
        isListingSupportedByTravelTime &&
        (neighbourhoodData || neighbourhoodLoading)
      "
      :neighbourhoodData="neighbourhoodData"
      :isLoading="neighbourhoodLoading"
      :listingId="listing.id ?? ''"
      @refetchWithPersona="handlePersonaChange"
      @refetchWithRefinement="handlePersonaRefinement"
    />
    <Description
      :listingDefaultLocale="listingDefaultLocale"
      @maskedContentClicked="emit('maskedContentClicked')"
    />
    <ListingLink
      v-if="listingLink"
      :listingLink="listingLink"
      @trackOutboundLink="trackOutboundLink"
    />
    <MortgageCalculatorContainer
      v-if="isMortgageCalculatorEnabled"
      :listing="listing"
      :isPlatformIs24="isPlatformIs24()"
    />
    <HgPromotionalCard
      v-if="showPromoteAccountCreationBanner"
      :badgeText="badgeText"
      :title="promotionalBannerTitle"
      :description="promotionalBannerDescription"
      :buttonText="promotionalBannerButtonText"
      :theme="platform"
      :imageUrl="promotionalCardImage"
      altText="Create your account"
      @promotionalButtonClick="handleRegister"
    />
    <div v-if="!shouldShowAgencyCard">
      <div id="viewingDetailsScrollAnchor" :class="$style.viewingDetailsScrollAnchor" />
      <ViewingDetails
        v-if="viewingPerson"
        :viewingPerson="viewingPerson"
        :viewingBlocked="contactBlocked"
        :listerId="listerId"
        @phoneNumberReveal="emit('phoneNumberRevealViewing', $event)"
        @revealedPhoneNumberClick="emit('revealedPhoneNumberClickViewing', $event)"
        @phoneNumberClickMobile="emit('phoneNumberClickMobileViewing', $event)"
        @phoneNumberRevealBlock="emit('phoneNumberViewingClickBlock')"
      />
    </div>
    <ListerBranding
      v-if="!shouldShowAgencyCard && listerBranding && !isAnibisTuttiBackFill && lister"
      :listerBranding="listerBranding"
      :listerId="lister.id"
      :listing="listing"
      :class="$style.column"
    />
    <ListerContactPhone
      v-if="!shouldShowAgencyCard && !isAnibisTuttiBackFill && lister"
      :class="$style.column"
      :lister="lister"
      @phoneNumberReveal="phoneNumberRevealContact"
      @revealedPhoneNumberClick="revealedPhoneNumberClickContact"
      @phoneNumberClickMobile="phoneNumberClickMobileContact"
      @phoneNumberRevealBlock="phoneNumberListerClickBlock"
    />
    <AgencyCard
      v-if="shouldShowAgencyCard && !isAnibisTuttiBackFill"
      :listing="listing"
      :agencyAgent="agencyAgent"
      :isOnTopOfContactForm="false"
      :listerBranding="listerBranding"
      @phoneNumberReveal="phoneNumberRevealContact"
      @revealedPhoneNumberClick="revealedPhoneNumberClickContact"
      @phoneNumberClickMobile="phoneNumberClickMobileContact"
      @phoneNumberRevealViewing="emit('phoneNumberRevealViewing', $event)"
      @revealedPhoneNumberClickViewing="emit('revealedPhoneNumberClickViewing', $event)"
      @phoneNumberClickMobileViewing="emit('phoneNumberClickMobileViewing', $event)"
    />
    <ListingTechReferences :listing="listing" />
  </section>
</template>

<script lang="ts" setup>
import { datadogRum } from '@datadog/browser-rum';
import { HgPromotionalCard } from '@smg-real-estate/domus';
import { L10N, Lister, Listing, Person, Url } from '@smg-real-estate/hg-rets-schema';
import { AgencyAgent, ListerBranding as ListerBrandingType } from '@smg-real-estate/hg-search-libs';
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onServerPrefetch,
  onUnmounted,
  onUpdated,
  ref,
  useSSRContext,
  watch,
} from 'vue';

import AddressDetails from '@/components/AddressDetails/AddressDetails.vue';
import { PhoneNumberType } from '@/components/ContactForm/contact-form-tracking-events';
import CoreAttributes from '@/components/CoreAttributes/CoreAttributes.vue';
import Costs from '@/components/Costs/Costs.vue';
import CTANoAddress from '@/components/CTANoAddress/CTANoAddress.vue';
import Description from '@/components/Description/Description.vue';
import FeaturesFurnishings from '@/components/FeaturesFurnishings/FeaturesFurnishings.vue';
import ListerBranding from '@/components/ListerBranding/ListerBranding.vue';
import ListerContactPhone from '@/components/ListerContactPhone/ListerContactPhone.vue';
import ListingAccessories from '@/components/ListingAccessories/ListingAccessories.vue';
import { BackFillAccountIds } from '@/components/ListingDetails/types';
import ListingDocuments from '@/components/ListingDocuments/ListingDocuments.vue';
import ListingLink from '@/components/ListingLink/ListingLink.vue';
import ListingStats from '@/components/ListingStats/ListingStats.vue';
import ListingTechReferences from '@/components/ListingTechReferences/ListingTechReferences.vue';
import ListingTitle from '@/components/ListingTitle/ListingTitle.vue';
import NeighbourhoodSection from '@/components/Neighbourhood/NeighbourhoodSection.vue';
import { isListingValidForMortgageCalculations } from '@/components/PartnerIntegrations';
import SpotlightAttributes from '@/components/SpotlightAttributes/SpotlightAttributes.vue';
import {
  isHighAccurateGeocoordinates,
  isListingSupportedByTravelTime as isListingSupportedByTravelTimeHelper,
} from '@/components/TravelTime/helper';
import TravelTime from '@/components/TravelTime/TravelTime.vue';
import ViewingDetails from '@/components/ViewingDetails/ViewingDetails.vue';
import { useNewBuildingObservers } from '@/composables/useNewBuildingObservers';
import { usePromotionalBanner } from '@/composables/usePromotionalBanner';
import FeatureFlags from '@/helpers/FeatureToggle/FeatureFlags';
import { isFeatureEnabled } from '@/helpers/FeatureToggle/FeatureToggleHelper';
import {
  getAvailability,
  getListingDefaultLocale,
  getListingLink,
} from '@/helpers/ListingData/ListingData';
import {
  DEFAULT_PERSONA,
  NEIGHBOURHOOD_FALLBACK,
} from '@/helpers/Neighbourhood/neighbourhoodConstants';
import { getPersonaConfig } from '@/helpers/Neighbourhood/personaTypes';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { NewConstructionDataWithOtherListings } from '@/models';
import { SplitFeatureFlags } from '@/splitio/splitFeatureFlags';
import { useNeighbourhoodStore } from '@/store/modules/neighbourhood';
import { useNewConstructionStore } from '@/store/modules/new-construction';
import { useSplitStore } from '@/store/modules/split';
import { useTravelTimeStore } from '@/store/modules/travel-time';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const ssrContext = import.meta.env.SSR ? useSSRContext() : undefined;
const splitStore = useSplitStore();
const travelTimeStore = useTravelTimeStore();
const neighbourhoodStore = useNeighbourhoodStore();
const route = useRoute();
const { t, locale } = useI18n();
const newConstructionStore = useNewConstructionStore();

const isTreatmentOn = (flag: SplitFeatureFlags) => splitStore.isTreatmentOn(flag);

defineOptions({
  name: 'ListingDetails',
});

const AgencyCard = defineAsyncComponent(() => import('@/components/AgencyCard/AgencyCard.vue'));
const TravelTimeCustomPOI = defineAsyncComponent(
  () => import('@/components/TravelTime/TravelTimeCustomPOI.vue'),
);
const NewBuildingProjectCard = defineAsyncComponent(
  () => import('@/components/NewBuildingProjectCard/NewBuildingProjectCard.vue'),
);
const NewBuildingProjectCardSticky = defineAsyncComponent(
  () => import('@/components/NewBuildingProjectCardSticky/NewBuildingProjectCardSticky.vue'),
);
const TenantPlusBadge = defineAsyncComponent(
  () => import('@/components/TenantPlus/Badge/TenantPlusBadge.vue'),
);
const NewBuildingStickyBar = defineAsyncComponent(
  () => import('@/components/NewBuildingStickyBar/NewBuildingStickyBar.vue'),
);
const MortgageCalculatorContainer = defineAsyncComponent(
  () =>
    import('@/components/PartnerIntegrations/MortgageCalculator/MortgageCalculatorContainer.vue'),
);

interface Props {
  listing: Listing;
  agencyAgent?: AgencyAgent | null;
  listerBranding?: ListerBrandingType | null;
  isTenantPlusSubscriber?: boolean;
  isPreviewMode?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  agencyAgent: null,
  listerBranding: null,
  isTenantPlusSubscriber: false,
  isPreviewMode: false,
});

const emit = defineEmits<{
  virtualTourClick: [];
  videoClick: [];
  documentClick: [];
  onRequestAddress: [];
  phoneNumberViewingClickBlock: [];
  phoneNumberRevealViewing: [listerPhoneNumberType: PhoneNumberType];
  revealedPhoneNumberClickViewing: [listerPhoneNumberType: PhoneNumberType];
  phoneNumberClickMobileViewing: [listerPhoneNumberType: PhoneNumberType];
  trackOutboundLink: [externalLink: Url];
  phoneNumberRevealContact: [listerPhoneNumberType: PhoneNumberType];
  revealedPhoneNumberClickContact: [listerPhoneNumberType: PhoneNumberType];
  phoneNumberClickMobileContact: [listerPhoneNumberType: PhoneNumberType];
  phoneNumberListerClickBlock: [listerPhoneNumberType: PhoneNumberType];
  scrollToOtherListings: [];
  maskedContentClicked: [];
  savePriceDropAlert: [event: unknown];
}>();

const {
  isNewBuildingProjectCardInViewport,
  isNewBuildingProjectCardOnTop,
  setupObservers,
  cleanupObservers,
} = useNewBuildingObservers();
const {
  promotionalCardImage,
  showPromoteAccountCreationBanner,
  platform,
  badgeText,
  promotionalBannerDescription,
  promotionalBannerButtonText,
  promotionalBannerTitle,
  handleRegister,
  setPromotionalCardImage,
} = usePromotionalBanner();

const isListingStatsComponentVisible = ref(false);
const newBuildingProjectCardContainer = ref<Element | undefined>(undefined);
const newBuildingStickyBar = ref<Element | undefined>(undefined);

if (import.meta.env.SSR) {
  onServerPrefetch(async () => {
    if (ssrContext && props.listing) {
      ssrContext.listing = props.listing;
    }
    if (isHighAccurateGeocoordinates(props.listing)) {
      try {
        await travelTimeStore.loadCustomPOI({
          listings: [props.listing],
        });
      } catch (error) {
        datadogRum.addError(error);
      }
    }
  });
}

const spotlightComponents = computed(
  (): Array<typeof SpotlightAttributes | typeof ListingTitle> => {
    if (isPlatformIs24()) {
      return [SpotlightAttributes, ListingTitle];
    } else {
      return [ListingTitle, SpotlightAttributes];
    }
  },
);
const lister = computed((): Lister | undefined => {
  return props.listing.lister;
});
const viewingPerson = computed((): Person | null | undefined => {
  return props.listing.lister?.contacts?.viewing;
});
const listerId = computed((): string | undefined => {
  return props.listing.lister?.id;
});
const newConstructionProject = computed(
  (): NewConstructionDataWithOtherListings | null | undefined => {
    return newConstructionStore.newConstructionData;
  },
);
const listingDefaultLocale = computed((): L10N | undefined => {
  return getListingDefaultLocale(props.listing);
});
const listingLink = computed((): Url | null => {
  return getListingLink(listingDefaultLocale.value ?? {});
});
const shouldShowAgencyCard = computed((): boolean => {
  return !!props.agencyAgent && !!props.listerBranding?.isPremiumBranding;
});
const isAnibisTuttiBackFill = computed((): boolean => {
  return (
    props.listing.lister?.id === BackFillAccountIds.ANIBIS ||
    props.listing.lister?.id === BackFillAccountIds.TUTTI
  );
});
const contactBlocked = computed((): boolean => {
  return props.listing.lister?.allowToContact === false;
});
const isMortgageCalculatorEnabled = computed((): boolean => {
  return isListingValidForMortgageCalculations(props.listing);
});
const showListingStats = computed((): boolean => {
  return !props.isPreviewMode && isListingStatsComponentVisible.value;
});
const availableDate = computed((): string => {
  return getAvailability(props.listing, t);
});
const isTenantPlusListing = computed((): boolean | undefined => {
  return props.listing.valueAddedServices?.isTenantPlusListing;
});
const showTravelTimeCustomPOI = computed((): boolean => {
  return !props.isPreviewMode;
});

const isListingSupportedByTravelTime = computed((): boolean => {
  return isListingSupportedByTravelTimeHelper(props.listing);
});

const enableNeighbourhood = computed((): boolean => {
  return isTreatmentOn(SplitFeatureFlags.KK_1913_Neighbourhood_AI_Guide);
});

const enableNeighbourhoodWebSearch = computed((): boolean => {
  if (!route) return false;
  return isFeatureEnabled(FeatureFlags.ENABLE_NEIGHBOURHOOD_WEBSEARCH, route);
});

const neighbourhoodLoading = computed((): boolean => {
  return neighbourhoodStore.isLoading;
});

const neighbourhoodData = computed((): any => {
  const storeData = neighbourhoodStore.neighbourhoodData;
  if (storeData) {
    return storeData;
  }
  return {
    locationName: props.listing.address?.locality || NEIGHBOURHOOD_FALLBACK.LOCATION_NAME,
    insights: {
      description: NEIGHBOURHOOD_FALLBACK.DESCRIPTION,
      tags: NEIGHBOURHOOD_FALLBACK.TAGS,
    },
  };
});

const getBackendLocale = (): string => {
  return (locale.value as string).toLowerCase().split('-')[0];
};

const fetchNeighbourhoodDataIfEnabled = (persona?: string) => {
  if (!enableNeighbourhood.value || !isListingSupportedByTravelTime.value) return;

  const { latitude, longitude } = props.listing.address?.geoCoordinates || {};
  if (!latitude || !longitude) return;

  neighbourhoodStore.fetchNeighbourhoodData({
    latitude,
    longitude,
    municipalityName: props.listing.address?.locality,
    propertyType: props.listing.characteristics?.livingSpace
      ? `${props.listing.characteristics.livingSpace}m² ${props.listing.categories?.[0]?.toLowerCase() || 'apartment'}`
      : 'Residential property',
    zipCode: props.listing.address?.postalCode,
    persona: persona || DEFAULT_PERSONA,
    locale: getBackendLocale(),
    listingId: props.listing.id ?? '',
    enableWebSearch: enableNeighbourhoodWebSearch.value,
    t,
  });
};

const handlePersonaChange = (persona: string) => {
  fetchNeighbourhoodDataIfEnabled(persona);
};

const handlePersonaRefinement = (payload: {
  persona: string;
  refinementAnswers: Record<string, string>;
}) => {
  const { latitude, longitude } = props.listing.address?.geoCoordinates || {};
  if (!latitude || !longitude) return;

  neighbourhoodStore.fetchNeighbourhoodData({
    latitude,
    longitude,
    municipalityName: props.listing.address?.locality,
    propertyType: props.listing.characteristics?.livingSpace
      ? `${props.listing.characteristics.livingSpace}m² ${props.listing.categories?.[0]?.toLowerCase() || 'apartment'}`
      : 'Residential property',
    zipCode: props.listing.address?.postalCode,
    persona: payload.persona,
    refinementAnswers: payload.refinementAnswers,
    locale: getBackendLocale(),
    listingId: props.listing.id ?? '',
    t,
  });
};

setPromotionalCardImage();

onMounted(async () => {
  const currentPersona =
    getPersonaConfig(neighbourhoodStore.selectedPersona)?.label || DEFAULT_PERSONA;
  fetchNeighbourhoodDataIfEnabled(currentPersona);
  // Apply client side rendering for ListingStats component as SEO requirement
  isListingStatsComponentVisible.value = true;

  // SSR safety: Ensure DOM is fully hydrated before setting up observers
  await nextTick();

  if (newBuildingProjectCardContainer.value && newBuildingStickyBar.value) {
    setupObservers(newBuildingProjectCardContainer.value, newBuildingStickyBar.value);
  }
});

onUpdated(async () => {
  // SSR safety: Use nextTick to ensure DOM updates are complete
  await nextTick();

  if (newBuildingProjectCardContainer.value && newBuildingStickyBar.value) {
    setupObservers(newBuildingProjectCardContainer.value, newBuildingStickyBar.value);
  }
});

onUnmounted(() => {
  cleanupObservers();
});

// KK-2234: Refetch neighbourhood data when locale changes, preserving the user's persona selection
watch(locale, (newLocale, oldLocale) => {
  if (newLocale !== oldLocale) {
    const currentPersona =
      getPersonaConfig(neighbourhoodStore.selectedPersona)?.label || DEFAULT_PERSONA;
    fetchNeighbourhoodDataIfEnabled(currentPersona);
  }
});

const trackOutboundLink = (externalLink: Url): void => {
  emit('trackOutboundLink', externalLink);
};
const phoneNumberRevealContact = (listerPhoneNumberType: PhoneNumberType): void => {
  emit('phoneNumberRevealContact', listerPhoneNumberType);
};
const revealedPhoneNumberClickContact = (listerPhoneNumberType: PhoneNumberType): void => {
  emit('revealedPhoneNumberClickContact', listerPhoneNumberType);
};
const phoneNumberClickMobileContact = (listerPhoneNumberType: PhoneNumberType): void => {
  emit('phoneNumberClickMobileContact', listerPhoneNumberType);
};
const phoneNumberListerClickBlock = (listerPhoneNumberType: PhoneNumberType): void => {
  emit('phoneNumberListerClickBlock', listerPhoneNumberType);
};

const scrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};
</script>

<style module lang="scss">
@import '@/assets/variables';

$detail-blocks-spacing: $space-200;

.row {
  display: flex;
  flex-flow: row wrap;
}

.column {
  flex: 0 0 100%;

  + .column {
    margin-top: $detail-blocks-spacing;
  }
}

.viewingDetailsScrollAnchor {
  position: relative;
  top: -$space-100;
  visibility: hidden;
}

.spotlightContainer {
  display: flex;
  align-items: flex-start;
  width: 100%;
  border-bottom: 1px solid $color-grey-50;
}

@media (min-width: $viewport-md-min) {
  .column {
    flex: 1 0 auto;

    + .column {
      margin-top: 0;
    }

    &:first-child {
      margin-right: $detail-blocks-spacing;
    }
  }

  .newBuildingProjectCardSticky {
    display: none !important;
  }

  .newProjectCardContainer {
    position: -webkit-sticky; /* Safari compatibility */
    position: sticky;
    top: 0.75rem;
    z-index: 11;
  }

  .tenantPlusBadge {
    margin: $space-25 0 0 $space-100;
  }
}
</style>

<style scoped lang="scss">
@import '@/assets/variables';

$contact-form-width: 386px;
$detail-blocks-spacing: $space-200;
$print-section-items-spacing: $space-75;

.hg-listing-details {
  @media (min-width: $viewport-md-min) {
    flex-grow: 1;
    flex-shrink: 1;
    max-width: calc(100% - #{$contact-form-width} - #{$space-150});
  }

  margin-bottom: calc(#{$space-100} * 2);

  > *:not(:last-child) {
    margin-bottom: $detail-blocks-spacing;
  }
}

.listing-accessories {
  width: 100%;
  border-color: $color-grey-50;
  border-style: solid;
  border-width: 0 0 1px 0;
}

.spotlight-components {
  flex: 1;
  padding: $space-150 0;
  margin-top: -$space-150;

  > *:not(:last-child) {
    margin-bottom: $space-100;
  }
}

@media (min-width: $viewport-md-min) {
  .hg-listing-details {
    margin-right: $space-150;
    margin-bottom: 0;
  }

  .spotlight-components {
    > *:not(:last-child) {
      margin-bottom: $space-150;
    }
  }
}

@media print {
  .hg-listing-details > *:not(:last-child) {
    margin-bottom: $print-section-items-spacing;
  }
}
</style>
